import { computed, ref } from 'vue'

import type { LeafletProcessingStatusApiModel } from '@/api/leaflets'
import type { LeafletTabData } from '@/views/dashboard/components/AddEditLeafletDialog/domain/leaflet-form.types'

import { applyTaskChanges } from './applyTaskChanges'
import { createTaskExecutor } from './createTaskExecutor'
import type { CreationApi } from './creationApi'
import { errorMessage } from './creationErrors'
import { type CreationContext, type CreationTask, makeTask, needsClient, sameContext } from './creationTask'
import type { TaskPersistence } from './taskPersistence'

interface Dependencies {
  api: CreationApi
  persistence: TaskPersistence
  locks?: LockManager
}

export function createLeafletCreationStore({ api, persistence, locks }: Dependencies) {
  const tasks = ref<CreationTask[]>([])
  const context = ref<CreationContext | null>(null)
  const storageError = ref(false)
  const collapsed = ref(false)
  const working = new Set<string>()
  const ownedTasks = new Map<string, CreationTask>()
  const wakeups = new Map<string, () => void>()
  const listeners = new Set<(task: CreationTask) => void>()
  let timer: ReturnType<typeof setInterval> | undefined
  let polling = false

  function ensureTimer() {
    if (timer || !context.value) return
    timer = setInterval(() => {
      void tick().catch(() => {
        storageError.value = true
      })
    }, 1500)
  }

  function save(task: CreationTask) {
    try {
      persistence.save(task)
      storageError.value = false
    } catch (error) {
      storageError.value = true
      throw error
    }
    if (sameContext(context.value, task.context)) {
      tasks.value = tasks.value.some(current => current.id === task.id)
        ? tasks.value.map(current => (current.id === task.id ? task : current))
        : [...tasks.value, task]
      listeners.forEach(listener => listener(task))
    }
  }

  function refresh() {
    if (!context.value) return
    const stored = persistence.read(context.value)
    tasks.value = stored.map(task => {
      const memory = tasks.value.find(current => current.id === task.id)
      if (memory) {
        for (const field of ['fileList', 'firstPageFile'] as const) {
          task.tab[field] = task.tab[field].map(file => ({
            ...file,
            raw: memory.tab[field].find(previous => previous.uid === file.uid)?.raw
          }))
        }
      }
      return task
    })
    if (tasks.value.length) ensureTimer()
    else if (timer && !working.size) {
      clearInterval(timer)
      timer = undefined
    }
  }

  async function exclusive(name: string, action: () => Promise<void>) {
    if (locks) {
      let acquired = false
      await locks.request(`mg:leaflet-creation:${name}`, { ifAvailable: true }, async lock => {
        if (lock) {
          acquired = true
          await action()
        }
      })
      return acquired
    } else {
      // Never replay a persisted mutation in another tab without cross-tab exclusion.
      throw new Error('locksUnavailable')
    }
  }

  const taskExecutor = createTaskExecutor({ api, save, isActive: task => sameContext(context.value, task.context) })

  async function run(id: string) {
    if (!context.value || working.has(id) || working.size >= 4) return
    const scope = { ...context.value }
    working.add(id)
    let checkpoint!: () => void
    const started = new Promise<void>(resolve => {
      checkpoint = resolve
    })
    void exclusive(`${scope.country}:${scope.account}:${id}`, async () => {
      if (!sameContext(context.value, scope)) return
      refresh()
      const task = tasks.value.find(task => task.id === id)
      if (!task || !['queued', 'running', 'generating'].includes(task.state)) return
      ownedTasks.set(id, task)
      try {
        if (task.state !== 'generating') await taskExecutor.execute(task)
        checkpoint()
        // Hold ownership across generation so another tab cannot lose this tab's local cover files.
        while (sameContext(context.value, scope) && task.state === 'generating') {
          await new Promise<void>(resolve => {
            wakeups.set(id, resolve)
          })
          if (sameContext(context.value, scope) && ownedTasks.get(id)?.state === 'queued')
            await taskExecutor.execute(task)
        }
      } catch (error) {
        taskExecutor.stopWith(task, 'unknown', errorMessage(error))
      }
    })
      .catch(error => {
        const task = tasks.value.find(task => task.id === id)
        if (task && !storageError.value) taskExecutor.stopWith(task, 'attention', errorMessage(error))
      })
      .finally(() => {
        working.delete(id)
        ownedTasks.delete(id)
        wakeups.delete(id)
        checkpoint()
      })
    await started
  }

  function applyPolledStatus(task: CreationTask, statuses: LeafletProcessingStatusApiModel[], failed: boolean) {
    if (task.state !== 'generating') return
    if (failed) taskExecutor.stopWith(task, 'unknown', 'statusUnavailable')
    else
      taskExecutor.acceptStatus(
        task,
        statuses.find(status => status.leafletId === task.leafletId)
      )
    if (task.state !== 'generating') wakeups.get(task.id)?.()
  }

  async function poll() {
    if (polling || !context.value) return
    polling = true
    const scope = { ...context.value }
    try {
      await exclusive(`poll:${scope.country}:${scope.account}`, async () => {
        if (!sameContext(context.value, scope)) return
        refresh()
        const pending = tasks.value.filter(task => task.state === 'generating' && task.leafletId)
        if (!pending.length) return
        let statuses: LeafletProcessingStatusApiModel[] = []
        let failed = false
        try {
          statuses = await api.statuses(pending.map(task => task.leafletId!))
        } catch {
          failed = true
        }
        for (const item of pending) {
          if (!sameContext(context.value, scope)) return
          const owned = ownedTasks.get(item.id)
          if (owned) {
            applyPolledStatus(owned, statuses, failed)
          } else {
            await exclusive(`${scope.country}:${scope.account}:${item.id}`, async () => {
              refresh()
              const task = tasks.value.find(task => task.id === item.id)
              if (task) applyPolledStatus(task, statuses, failed)
            })
          }
        }
      })
    } finally {
      polling = false
    }
  }

  async function tick() {
    if (!context.value || storageError.value) return
    refresh()
    await poll()
    await Promise.all(
      tasks.value.filter(task => ['queued', 'running', 'generating'].includes(task.state)).map(task => run(task.id))
    )
  }

  function activate(next: CreationContext | null) {
    if (next && sameContext(context.value, next)) return
    context.value = next ? { ...next } : null
    wakeups.forEach(wake => wake())
    tasks.value = []
    storageError.value = false
    if (timer) clearInterval(timer)
    timer = undefined
    if (!next) return
    try {
      refresh()
    } catch {
      storageError.value = true
      return
    }
    void tick().catch(() => {
      storageError.value = true
    })
  }

  function enqueue(input: {
    tabs: LeafletTabData[]
    store: CreationTask['store']
    platforms: string[]
    isFromScrapper?: boolean
    brochureId?: string
  }) {
    if (!context.value) throw new Error('creationContextUnavailable')
    if (!locks) throw new Error('locksUnavailable')
    const additions = input.tabs.map(tab => makeTask({ ...input, tab, context: context.value! }))
    try {
      additions.forEach(task => persistence.save(task))
    } catch (error) {
      storageError.value = true
      additions.forEach(task => persistence.remove(task))
      throw error
    }
    tasks.value = [...tasks.value, ...additions]
    storageError.value = false
    ensureTimer()
    collapsed.value = false
    additions.forEach(task => {
      void run(task.id)
    })
    return additions.map(task => task.id)
  }

  async function retry(id: string) {
    const task = tasks.value.find(task => task.id === id)
    if (!task || !['attention', 'unknown', 'files'].includes(task.state)) return
    await exclusive(`${task.context.country}:${task.context.account}:${id}`, async () => {
      refresh()
      const current = tasks.value.find(task => task.id === id)
      if (!current || !['attention', 'unknown', 'files'].includes(current.state)) return
      await taskExecutor.prepareRetry(current)
    })
    await tick()
  }

  async function revise(id: string, tab: LeafletTabData) {
    const task = tasks.value.find(task => task.id === id)
    if (!task || !['attention', 'files'].includes(task.state)) throw new Error('operationBusy')
    const revised = await exclusive(`${task.context.country}:${task.context.account}:${id}`, async () => {
      refresh()
      const current = tasks.value.find(task => task.id === id)
      if (!current || !['attention', 'files'].includes(current.state)) throw new Error('operationBusy')
      applyTaskChanges(current, tab)
      save(current)
    })
    if (!revised) throw new Error('operationBusy')
    void retry(id).catch(() => {
      storageError.value = true
    })
  }

  async function dismiss(id: string) {
    const task = tasks.value.find(task => task.id === id)
    if (!task || !['done', 'attention', 'files', 'unknown'].includes(task.state)) return
    await exclusive(`${task.context.country}:${task.context.account}:${id}`, async () => {
      refresh()
      const current = tasks.value.find(task => task.id === id)
      if (!current || !['done', 'attention', 'files', 'unknown'].includes(current.state)) return
      persistence.remove(current)
      tasks.value = tasks.value.filter(task => task.id !== id)
    })
  }

  function subscribe(listener: (task: CreationTask) => void) {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }

  function dispose() {
    activate(null)
    listeners.clear()
  }

  return {
    tasks,
    context,
    collapsed,
    storageError,
    activate,
    enqueue,
    retry,
    revise,
    dismiss,
    subscribe,
    refresh,
    tick,
    dispose,
    hasClientWork: computed(() => tasks.value.some(needsClient)),
    activeCount: computed(
      () => tasks.value.filter(task => ['queued', 'running', 'generating'].includes(task.state)).length
    ),
    attentionCount: computed(
      () => tasks.value.filter(task => ['attention', 'files', 'unknown'].includes(task.state)).length
    )
  }
}

export type LeafletCreationStore = ReturnType<typeof createLeafletCreationStore>
